import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  ChevronDown,
  Ban,
  Handshake,
  CheckCircle2,
  ArrowRight,
  Circle,
  Pause,
} from "lucide-react";
import {
  type PaymentAction,
  getActionLabel,
  getActionColor,
} from "./paymentLineItems";

// ─── ACTION_OPTIONS (React nodes, used internally by ActionDropdown) ────
const ACTION_OPTIONS: { value: PaymentAction; label: string; shortLabel: string; icon: React.ReactNode }[] = [
  { value: "pending", label: "Select Action", shortLabel: "Select…", icon: <Circle size={13} /> },
  { value: "paid", label: "Paid", shortLabel: "Paid", icon: <CheckCircle2 size={13} /> },
  { value: "voided", label: "Voided", shortLabel: "Voided", icon: <Ban size={13} /> },
  { value: "held", label: "Held", shortLabel: "Held", icon: <Pause size={13} /> },
  { value: "agreement_made", label: "Agreement Made", shortLabel: "Agreement", icon: <Handshake size={13} /> },
  { value: "works_not_completed", label: "Works Not Completed", shortLabel: "Not Done", icon: <ArrowRight size={13} /> },
];

// ─── ActionDropdown Component ──────────────────────────────────
export function ActionDropdown({
  value,
  onChange,
}: {
  value: PaymentAction;
  onChange: (action: PaymentAction) => void;
}) {
  const [open, setOpen] = useState(false);
  const colors = getActionColor(value);
  const btnRef = useRef<HTMLButtonElement>(null);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number } | null>(null);

  const calculateDropdownPosition = () => {
    if (!btnRef.current) return null;
    const rect = btnRef.current.getBoundingClientRect();
    const dropdownWidth = 180;
    const dropdownHeight = 180;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top =
      spaceBelow > dropdownHeight
        ? rect.bottom + 4
        : Math.max(8, rect.top - dropdownHeight - 4);
    const left = Math.min(
      Math.max(8, rect.right - dropdownWidth),
      window.innerWidth - dropdownWidth - 8,
    );

    return { top, left };
  };

  const toggleDropdown = () => {
    if (open) {
      setOpen(false);
      return;
    }

    const nextPosition = calculateDropdownPosition();
    if (!nextPosition) return;
    setDropdownPos(nextPosition);
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    const updatePosition = () => {
      const nextPosition = calculateDropdownPosition();
      if (nextPosition) setDropdownPos(nextPosition);
    };

    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open]);

  return (
    <div style={{ position: "relative" }}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggleDropdown}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          padding: "3px 8px",
          borderRadius: 6,
          fontSize: "0.68rem",
          fontWeight: 600,
          border: `1px solid ${colors.border}`,
          background: colors.bg,
          color: colors.text,
          cursor: "pointer",
          transition: "all 0.15s",
          whiteSpace: "nowrap",
          maxWidth: "100%",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {ACTION_OPTIONS.find((o) => o.value === value)?.icon}
        {ACTION_OPTIONS.find((o) => o.value === value)?.shortLabel ?? getActionLabel(value)}
        <ChevronDown size={10} />
      </button>
      {open && dropdownPos && createPortal(
        <>
          <div
            style={{ position: "fixed", inset: 0, zIndex: 10010 }}
            onClick={() => setOpen(false)}
          />
          <div
            style={{
              position: "fixed",
              top: dropdownPos.top,
              left: dropdownPos.left,
              background: "#fff",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              zIndex: 10011,
              minWidth: 180,
              overflow: "hidden",
            }}
          >
            {ACTION_OPTIONS.map((opt) => {
              const optColors = getActionColor(opt.value);
              const isSelected = opt.value === value;
              return (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                  }}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    width: "100%",
                    padding: "9px 14px",
                    border: "none",
                    background: isSelected ? optColors.bg : "transparent",
                    color: isSelected ? optColors.text : "#334155",
                    fontSize: "0.78rem",
                    fontWeight: isSelected ? 600 : 500,
                    cursor: "pointer",
                    textAlign: "left",
                    transition: "background 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "#f8fafc";
                  }}
                  onMouseLeave={(e) => {
                    if (!isSelected) e.currentTarget.style.background = "transparent";
                  }}
                >
                  <span style={{ color: optColors.text }}>{opt.icon}</span>
                  {opt.label}
                </button>
              );
            })}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}
